import { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  Phone, Filter, RefreshCw, ChevronDown, ChevronRight,
  Calendar, MessageSquare, XCircle, Clock, Info, Trophy, StickyNote,
  Users, DollarSign, Wallet, LogIn, HelpCircle, UserCheck, UserX, Globe, User, Star,
  Mail, Snowflake, RotateCcw, AlertTriangle, Target, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { LogOutcomeDialog } from '@/components/admin/LogOutcomeDialog';
import { OutcomeHistory } from '@/components/admin/OutcomeHistory';
import { PartnerSearchLog } from '@/components/admin/PartnerSearchLog';
import { outcomeLabel } from '@/components/admin/outcomeApi';
import { ReengagementReport } from '@/components/admin/report/ReengagementReport';
import { ChipMultiSelect, REPORT_OUTCOMES, OUTCOME_COLORS } from '@/components/admin/report/reportShared';
import { BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return t ? { 'Authorization': `Bearer ${t}` } : {};
};

const BUCKETS = {
  1: { label: 'Lapsed referrer',   color: 'bg-red-100 text-red-700 border-red-200',          icon: Users,      help: 'Added referrals before but no NEW referral in the past 90 days' },
  2: { label: 'Lapsed earner',     color: 'bg-orange-100 text-orange-700 border-orange-200', icon: DollarSign, help: 'Drove revenue before but no NEW revenue-generating order in the past 90 days' },
  3: { label: 'Bank-ready, idle',  color: 'bg-amber-100 text-amber-700 border-amber-200',    icon: Wallet,     help: 'Added bank details but have zero referrals and zero revenue so far' },
  4: { label: 'New, no activity',  color: 'bg-blue-100 text-blue-700 border-blue-200',       icon: LogIn,      help: 'Logged in within the last 30 days but has zero referrals and zero revenue' },
  5: { label: 'Cold',              color: 'bg-gray-100 text-gray-700 border-gray-200',       icon: HelpCircle, help: 'Hasn\'t logged in recently and has no activity' },
};


// ------- Inline rating widget -------
function InlineRating({ partnerId, value, onChange, compact = true }) {
  const [hover, setHover] = useState(null);
  const [saving, setSaving] = useState(false);
  const current = hover ?? value ?? 0;

  const save = (next) => {
    setSaving(true);
    const xhr = new XMLHttpRequest();
    xhr.open('PATCH', `${API}/admin/partners/${partnerId}/rating`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setSaving(false);
      if (xhr.status === 200) {
        onChange?.(next);
        toast.success(next ? `Rated ${next}/5` : 'Rating cleared');
      } else toast.error('Failed to save rating');
    };
    xhr.onerror = () => { setSaving(false); toast.error('Network error'); };
    xhr.send(JSON.stringify({ rating: next }));
  };

  const starSize = compact ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div
      className="flex items-center gap-0.5"
      onMouseLeave={() => setHover(null)}
      data-testid={`rating-${partnerId}`}
      title="Influence rating — click to set, click same star to clear"
      onClick={(e) => e.stopPropagation()}
    >
      {[1, 2, 3, 4, 5].map(n => {
        const filled = current >= n;
        return (
          <button
            key={n}
            onMouseEnter={() => setHover(n)}
            onClick={() => save(value === n ? 0 : n)}
            disabled={saving}
            data-testid={`rating-${partnerId}-star-${n}`}
            className="p-0.5 hover:scale-110 transition disabled:opacity-50"
          >
            <Star className={`${starSize} ${filled ? 'fill-yellow-400 text-yellow-500' : 'text-gray-300'}`} />
          </button>
        );
      })}
    </div>
  );
}

// ------- Dashboard strip -------
function DashboardStrip({ stats, loading }) {
  const tiles = [
    { label: 'Contacted today', value: stats?.contacted_today ?? '—', icon: Phone, color: 'text-orange-600 bg-orange-50 border-orange-200', testId: 'stat-contacted' },
    { label: 'Will refer today', value: stats?.will_refer_today ?? '—', hint: stats?.will_refer_committed_total ? `${stats.will_refer_committed_total} referrals committed` : null, icon: UserCheck, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', testId: 'stat-will-refer' },
    { label: 'Meetings set', value: stats?.meetings_set_today ?? '—', hint: stats?.meeting_results_today ? `${stats.meeting_results_today} results logged` : null, icon: Calendar, color: 'text-indigo-600 bg-indigo-50 border-indigo-200', testId: 'stat-meetings' },
    { label: 'No-phone to email', value: stats?.queue_no_phone_available ?? '—', hint: `${stats?.emails_sent_today ?? 0} emails sent today`, icon: Mail, color: 'text-sky-600 bg-sky-50 border-sky-200', testId: 'stat-emails' },
    { label: 'Not interested / Cold', value: `${stats?.not_interested_today ?? 0} / ${stats?.no_response_today ?? 0}`, hint: `${stats?.cold_total ?? 0} total cold`, icon: Snowflake, color: 'text-gray-600 bg-gray-50 border-gray-200', testId: 'stat-cold' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="reengagement-dashboard">
      {tiles.map(t => {
        const Icon = t.icon;
        return (
          <div key={t.label} className={`p-3 rounded-lg border ${t.color}`} data-testid={t.testId}>
            <div className="flex items-center gap-2 mb-1">
              <Icon className="w-4 h-4" />
              <span className="text-xs font-medium">{t.label}</span>
            </div>
            <div className="text-2xl font-bold">{loading ? '…' : t.value}</div>
            {t.hint && <div className="text-xs opacity-70 truncate" title={t.hint}>{t.hint}</div>}
          </div>
        );
      })}
    </div>
  );
}

export default function AdminReengagement() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    bucket: [],
    category: '',
    city: '',
    location: '',
    last_contacted_from: '',
    last_contacted_to: '',
    follow_up_due: false,
    last_outcome: [],
  });
  const [expanded, setExpanded] = useState({});
  const [historyKey, setHistoryKey] = useState(0);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [scope, setScope] = useState('mine');
  const [view, setView] = useState('active');  // 'active' | 'cold' | 'not_interested'

  // Email composer
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailPartner, setEmailPartner] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [emailSending, setEmailSending] = useState(false);

  const fetchQueue = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filters.bucket.length) qs.set('bucket', filters.bucket.join(','));
    if (filters.category) qs.set('category', filters.category);
    if (filters.city) qs.set('city', filters.city);
    if (filters.location) qs.set('location', filters.location);
    if (filters.last_contacted_from) qs.set('last_contacted_from', filters.last_contacted_from);
    if (filters.last_contacted_to) qs.set('last_contacted_to', filters.last_contacted_to);
    if (filters.follow_up_due) qs.set('follow_up_due', 'true');
    if (filters.last_outcome.length) qs.set('last_outcome', filters.last_outcome.join(','));
    qs.set('scope', scope);
    qs.set('show', view);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/admin/reengagement/queue?${qs.toString()}`, true);
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setLoading(false);
      if (xhr.status === 200) setData(JSON.parse(xhr.responseText));
      else toast.error('Failed to load re-engagement queue');
    };
    xhr.onerror = () => { setLoading(false); toast.error('Network error'); };
    xhr.send();
  };

  const fetchStats = () => {
    setStatsLoading(true);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/admin/reengagement/dashboard?scope=${scope}`, true);
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setStatsLoading(false);
      if (xhr.status === 200) setStats(JSON.parse(xhr.responseText));
    };
    xhr.onerror = () => setStatsLoading(false);
    xhr.send();
  };

  useEffect(() => {
    if (view === 'report') return;
    fetchQueue(); fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, scope, view]);

  const toggleBucket = (b) => {
    setFilters(f => ({ ...f, bucket: f.bucket.includes(b) ? f.bucket.filter(x => x !== b) : [...f.bucket, b] }));
  };
  const clearFilters = () => setFilters({ bucket: [], category: '', city: '', location: '', last_contacted_from: '', last_contacted_to: '', follow_up_due: false, last_outcome: [] });

  const toggleExpand = (partnerId) => setExpanded(e => ({ ...e, [partnerId]: !e[partnerId] }));

  const afterChange = () => { setHistoryKey(k => k + 1); fetchQueue(); fetchStats(); };

  const openOutcome = (partner) => {
    setSelectedPartner(partner);
    setOutcomeOpen(true);
  };

  // Email composer
  const openEmail = (partner) => {
    setEmailPartner(partner);
    setEmailForm({ subject: '', body: '' });
    setEmailOpen(true);
    // Pre-fill template
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/admin/reengagement/email-template/${partner.user_id}`, true);
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      if (xhr.status === 200) {
        const d = JSON.parse(xhr.responseText);
        setEmailForm({ subject: d.subject || '', body: d.body || '' });
      }
    };
    xhr.send();
  };

  const sendEmail = () => {
    if (!emailPartner) return;
    if (!emailForm.subject.trim() || !emailForm.body.trim()) return toast.error('Subject and body required');
    setEmailSending(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/admin/reengagement/send-email`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setEmailSending(false);
      if (xhr.status === 200) {
        const d = JSON.parse(xhr.responseText);
        toast.success(d.delivered ? 'Email sent' : 'Logged (delivery failed — check SendGrid)');
        setEmailOpen(false);
        fetchQueue();
        fetchStats();
      } else {
        toast.error('Failed to send');
      }
    };
    xhr.onerror = () => { setEmailSending(false); toast.error('Network error'); };
    xhr.send(JSON.stringify({ partner_id: emailPartner.user_id, subject: emailForm.subject, body: emailForm.body }));
  };

  // Re-queue a cold partner
  const requeue = (partner) => {
    if (!window.confirm(`Bring ${partner.name || partner.email} back into the daily queue?`)) return;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/admin/reengagement/requeue/${partner.user_id}`, true);
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      if (xhr.status === 200) {
        toast.success('Partner re-queued');
        fetchQueue();
        fetchStats();
      } else {
        toast.error('Failed to re-queue');
      }
    };
    xhr.send();
  };

  // One-shot: replay all historical outcomes to normalize suppression state.
  // Any admin can trigger — safe & idempotent.
  const [backfilling, setBackfilling] = useState(false);

  const runBackfill = () => {
    if (!window.confirm(
      'Replay ALL historical outcomes to normalize suppression state?\n\n' +
      'This re-computes cold flag, not-interested flag, and cooldown dates for every partner based on their outcome history. ' +
      'Safe to run multiple times. Typically needed once after deploying the cooldown rules.'
    )) return;
    setBackfilling(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/admin/reengagement/backfill-cooldowns`, true);
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setBackfilling(false);
      if (xhr.status === 200) {
        const d = JSON.parse(xhr.responseText);
        toast.success(
          `Done — ${d.outcomes_processed} outcomes replayed · ` +
          `${d.cold} marked cold · ${d.not_interested} not interested · ${d.with_cooldown} on cooldown`
        );
        fetchQueue();
        fetchStats();
      } else if (xhr.status === 403) {
        toast.error('Super admin only');
      } else {
        toast.error('Backfill failed');
      }
    };
    xhr.onerror = () => { setBackfilling(false); toast.error('Network error'); };
    xhr.send();
  };

  const bucketOrder = useMemo(() => Object.keys(BUCKETS).map(Number), []);

  const viewLabel = view === 'cold' ? 'Cold list' : view === 'not_interested' ? 'Not interested' : 'Daily queue';

  return (
    <div>
      <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Phone className="w-6 h-6 text-orange-500" />
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Re-engagement</h1>
          </div>
          <p className="text-muted-foreground mt-1">
            {view === 'active' ? (
              scope === 'mine'
                ? `Your daily list${data?.scoped_to ? ` for ${data.scoped_to}` : ''}: ${data?.todays_new ?? 0} new today + ${data?.carryover_count ?? 0} carried over. Rotates at midnight IST.`
                : `All partners (shared view across the team).`
            ) : view === 'cold' ? 'Partners marked cold after 2+ no-response. Re-queue when ready to retry.'
              : view === 'report' ? 'Interactions logged by the team over any period — by person, by day and by outcome.'
              : 'Partners who said they are not interested. Hidden from the daily queue.'}
          </p>
          {view !== 'report' && <p className="text-xs text-muted-foreground mt-1">
            "No response" takes the partner out of today's queue — they come back tomorrow and move to <strong>Cold</strong> if the next day also gets no response. "Note only" / "Wants more info" keep the partner in today's queue. "Will refer" hides for 30 days, "Meeting set up" hides until the meeting date and auto-resurfaces as "Post-meeting due", "Not interested" is permanent.
          </p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 p-1 bg-muted rounded-md" data-testid="scope-toggle">
            <button onClick={() => setScope('mine')} data-testid="scope-mine" className={`px-3 py-1 rounded text-sm flex items-center gap-1.5 ${scope === 'mine' ? 'bg-white shadow text-orange-600 font-medium' : 'text-muted-foreground'}`}>
              <User className="w-3.5 h-3.5" /> My queue
            </button>
            <button onClick={() => setScope('all')} data-testid="scope-all" className={`px-3 py-1 rounded text-sm flex items-center gap-1.5 ${scope === 'all' ? 'bg-white shadow text-orange-600 font-medium' : 'text-muted-foreground'}`}>
              <Globe className="w-3.5 h-3.5" /> All
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={() => { if (view === 'report') { setHistoryKey(k => k + 1); return; } fetchQueue(); fetchStats(); }} data-testid="refresh-queue-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setFiltersOpen(o => !o)} data-testid="toggle-filters-btn">
            <Filter className="w-4 h-4 mr-1.5" /> Filters
          </Button>
          <Button variant="outline" size="sm" onClick={runBackfill} disabled={backfilling}
            data-testid="backfill-cooldowns-btn"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            title="Replay all historical outcomes to normalize suppression state across every partner. Safe to run multiple times.">
            <RotateCcw className={`w-4 h-4 mr-1.5 ${backfilling ? 'animate-spin' : ''}`} />
            {backfilling ? 'Syncing...' : 'Sync suppression'}
          </Button>
        </div>
      </div>

      <PartnerSearchLog onLogged={() => { setHistoryKey(k => k + 1); fetchQueue(); fetchStats(); }} />

      {/* View tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-md w-fit mb-4" data-testid="view-tabs">
        <button onClick={() => setView('active')} data-testid="view-active" className={`px-3 py-1 rounded text-sm flex items-center gap-1.5 ${view === 'active' ? 'bg-white shadow text-orange-600 font-medium' : 'text-muted-foreground'}`}>
          <Phone className="w-3.5 h-3.5" /> Daily queue
        </button>
        <button onClick={() => setView('cold')} data-testid="view-cold" className={`px-3 py-1 rounded text-sm flex items-center gap-1.5 ${view === 'cold' ? 'bg-white shadow text-orange-600 font-medium' : 'text-muted-foreground'}`}>
          <Snowflake className="w-3.5 h-3.5" /> Cold {stats?.cold_total > 0 && <Badge variant="outline" className="h-5 px-1.5 text-xs">{stats.cold_total}</Badge>}
        </button>
        <button onClick={() => setView('not_interested')} data-testid="view-not-interested" className={`px-3 py-1 rounded text-sm flex items-center gap-1.5 ${view === 'not_interested' ? 'bg-white shadow text-orange-600 font-medium' : 'text-muted-foreground'}`}>
          <XCircle className="w-3.5 h-3.5" /> Not interested {stats?.not_interested_total > 0 && <Badge variant="outline" className="h-5 px-1.5 text-xs">{stats.not_interested_total}</Badge>}
        </button>
        <button onClick={() => setView('report')} data-testid="view-report" className={`px-3 py-1 rounded text-sm flex items-center gap-1.5 ${view === 'report' ? 'bg-white shadow text-orange-600 font-medium' : 'text-muted-foreground'}`}>
          <BarChart3 className="w-3.5 h-3.5" /> Report
        </button>
      </div>

      {view === 'report' && <ReengagementReport refreshKey={historyKey} />}

      {/* Dashboard strip */}
      {view === 'active' && <DashboardStrip stats={stats} loading={statsLoading} />}

      {/* Bucket chips (only on active view) */}
      {view === 'active' && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="bucket-stats">
          {bucketOrder.map(b => {
            const cfg = BUCKETS[b];
            const Icon = cfg.icon;
            const count = data?.bucket_counts?.[b] ?? 0;
            const active = filters.bucket.includes(b);
            return (
              <button key={b} onClick={() => toggleBucket(b)} data-testid={`bucket-chip-${b}`}
                className={`p-3 rounded-lg border text-left transition ${active ? 'ring-2 ring-orange-400 border-orange-300' : 'hover:border-orange-200'}`}
                title={cfg.help}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4 text-orange-500" />
                  <span className="text-xs font-medium text-muted-foreground">B{b}</span>
                </div>
                <div className="text-2xl font-bold">{count}</div>
                <div className="text-xs text-muted-foreground truncate">{cfg.label}</div>
              </button>
            );
          })}
        </div>
      )}

      {filtersOpen && view !== 'report' && (
        <Card className="p-4 mb-4" data-testid="filter-panel">
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Input value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })} data-testid="filter-category" placeholder="nutritionist, doctor..." />
            </div>
            <div>
              <Label className="text-xs">City</Label>
              <Input value={filters.city} onChange={e => setFilters({ ...filters, city: e.target.value })} data-testid="filter-city" placeholder="Mumbai" />
            </div>
            <div>
              <Label className="text-xs">Location</Label>
              <select value={filters.location} onChange={e => setFilters({ ...filters, location: e.target.value })} data-testid="filter-location" className="w-full mt-2 border rounded-md p-2 text-sm">
                <option value="">All</option>
                <option value="India">India</option>
                <option value="Dubai">Dubai</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm" data-testid="filter-follow-up-due-label">
                <input type="checkbox" checked={filters.follow_up_due} onChange={e => setFilters({ ...filters, follow_up_due: e.target.checked })} data-testid="filter-follow-up-due" />
                Follow-up due today/overdue
              </label>
            </div>
            <div>
              <Label className="text-xs">Last contacted from</Label>
              <Input type="date" value={filters.last_contacted_from} onChange={e => setFilters({ ...filters, last_contacted_from: e.target.value })} data-testid="filter-lc-from" />
            </div>
            <div>
              <Label className="text-xs">Last contacted to</Label>
              <Input type="date" value={filters.last_contacted_to} onChange={e => setFilters({ ...filters, last_contacted_to: e.target.value })} data-testid="filter-lc-to" />
            </div>
            <div className="flex items-end">
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">Clear filters</Button>
            </div>
          </div>
        </Card>
      )}

      {view !== 'report' && (
        <div className="flex items-start gap-2 mb-3 flex-wrap" data-testid="last-outcome-filter">
          <span className="text-xs font-medium text-muted-foreground mt-1.5 whitespace-nowrap">Last outcome:</span>
          <div className="flex-1">
            <ChipMultiSelect
              options={[{ key: 'fresh', label: '✨ Never contacted' }, ...REPORT_OUTCOMES.map(o => ({ ...o, color: OUTCOME_COLORS[o.key] }))]}
              selected={filters.last_outcome}
              onChange={(v) => setFilters(f => ({ ...f, last_outcome: v }))}
              testPrefix="queue-outcome"
            />
          </div>
        </div>
      )}

      {view !== 'report' && <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading...</div>
        ) : !data || data.partners.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">
            {view === 'cold' ? 'No cold partners.' : view === 'not_interested' ? 'No "not interested" partners.' : 'No partners match. Try clearing filters or switching to All.'}
          </div>
        ) : (
          <div className="divide-y">
            <div className="px-4 py-2 text-xs text-muted-foreground bg-muted/40 flex items-center justify-between flex-wrap gap-2">
              <span>
                {viewLabel}: <strong>{data.shown}</strong> partner(s)
                {view === 'active' && data.carryover_count > 0 && (
                  <>· <strong className="text-indigo-700">{data.todays_new} new today</strong> + <strong className="text-indigo-700">{data.carryover_count} carried over</strong></>
                )}
              </span>
              {view === 'active' && <span className="hidden sm:inline">Rotates at midnight IST · Deterministic</span>}
            </div>
            {data.partners.map(p => {
              const cfg = BUCKETS[p.bucket];
              const isOpen = expanded[p.user_id];
              const noPhone = !(p.phone && String(p.phone).trim());
              return (
                <div key={p.user_id} data-testid={`partner-row-${p.user_id}`}>
                  <div className="p-4 hover:bg-muted/30">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <button onClick={() => toggleExpand(p.user_id)} data-testid={`expand-${p.user_id}`} className="mt-0.5">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{p.name || '—'}</span>
                            <InlineRating partnerId={p.user_id} value={p.star_rating} onChange={(nv) => {
                              setData(d => ({ ...d, partners: d.partners.map(x => x.user_id === p.user_id ? { ...x, star_rating: nv } : x) }));
                            }} />
                            <Badge className={`${cfg.color} border`} variant="outline" title={cfg.help}>
                              B{p.bucket} · {cfg.label}
                            </Badge>
                            {p.post_meeting_due && (
                              <Badge className="bg-purple-100 text-purple-700 border-purple-200" variant="outline" title="Meeting date has passed — log the result">
                                <Target className="w-3 h-3 mr-1 inline" /> Post-meeting due
                              </Badge>
                            )}
                            {!p.latest_outcome && !p.cold && !p.not_interested_at && (
                              <Badge className="bg-green-100 text-green-700 border-green-200" variant="outline" title="Never been contacted — fresh lead">
                                ✨ Fresh
                              </Badge>
                            )}
                            {p.cold && <Badge className="bg-gray-200 text-gray-800 border-gray-300" variant="outline"><Snowflake className="w-3 h-3 mr-1 inline" /> Cold</Badge>}
                            {p.not_interested_at && <Badge className="bg-red-100 text-red-700 border-red-200" variant="outline">Not interested</Badge>}
                            {p.category && <Badge variant="outline" className="text-xs">{p.category}</Badge>}
                            {p.location && <Badge variant="outline" className="text-xs">{p.location}</Badge>}
                            {p.dormant && <Badge className="bg-red-100 text-red-700 border-red-200" variant="outline">Dormant</Badge>}
                            {p.carried_from && (
                              <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200" variant="outline">
                                ↩ Carried from {new Date(p.carried_from).toLocaleDateString()}
                              </Badge>
                            )}
                            {p.potential_referrals_committed > 0 && (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline">
                                🎯 {p.potential_referrals_committed} committed
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                            <span>{p.email}</span>
                            {p.phone ? <span>📞 {p.phone}</span> : <span className="text-sky-700 font-medium">✉ No phone — email only</span>}
                            {p.city && <span>📍 {p.city}</span>}
                          </div>
                          <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-x-4">
                            <span>Referrals: <strong>{p.referrals_total}</strong>{p.latest_referral_at && ` · last ${new Date(p.latest_referral_at).toLocaleDateString()}`}</span>
                            <span>Orders: <strong>{p.revenue_orders_total}</strong>{p.latest_revenue_at && ` · last ${new Date(p.latest_revenue_at).toLocaleDateString()}`}</span>
                            <span>Last login: {p.last_login ? new Date(p.last_login).toLocaleDateString() : 'never'}</span>
                            {p.samples_received ? <span className="text-green-700">📦 Samples received</span> : <span className="text-amber-700">📦 Samples pending</span>}
                            {p.bank_name && <span>🏦 {p.bank_name} ····{p.account_number_last4}</span>}
                          </div>
                          {p.latest_outcome && (
                            <div className="mt-2 text-xs bg-orange-50 border border-orange-200 rounded p-2">
                              <strong>Last outcome:</strong> {outcomeLabel(p.latest_outcome.outcome_type)}
                              {p.latest_outcome.note && <> — "{p.latest_outcome.note}"</>}
                              {p.next_followup_at && <> · <em>Follow-up: {new Date(p.next_followup_at).toLocaleDateString()}</em></>}
                              <span className="text-muted-foreground"> · {p.latest_outcome.logged_by} on {new Date(p.latest_outcome.created_at).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {view !== 'active' && (p.cold || p.not_interested_at) && (
                          <Button onClick={() => requeue(p)} data-testid={`requeue-${p.user_id}`} variant="outline" size="sm" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                            <RotateCcw className="w-4 h-4 mr-1.5" /> Re-queue
                          </Button>
                        )}
                        {noPhone && !p.cold && !p.not_interested_at && (
                          <Button onClick={() => openEmail(p)} data-testid={`email-${p.user_id}`} variant="outline" size="sm" className="border-sky-300 text-sky-700 hover:bg-sky-50">
                            <Mail className="w-4 h-4 mr-1.5" /> Email
                          </Button>
                        )}
                        <Button onClick={() => openOutcome(p)} data-testid={`log-outcome-${p.user_id}`} className="bg-orange-500 hover:bg-orange-600 text-white">
                          <MessageSquare className="w-4 h-4 mr-1.5" /> Log outcome
                        </Button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="mt-3 ml-7 bg-muted/30 rounded p-3" data-testid={`history-${p.user_id}`}>
                        <div className="text-xs font-semibold mb-2">Outreach history</div>
                        <OutcomeHistory partnerId={p.user_id} refreshKey={historyKey} onChanged={() => { fetchQueue(); fetchStats(); }} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>}

      <LogOutcomeDialog open={outcomeOpen} onOpenChange={setOutcomeOpen} partner={selectedPartner} onSaved={afterChange} />

      {/* Email composer */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-xl" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle>Email {emailPartner?.name || emailPartner?.email}</DialogTitle>
            <DialogDescription>
              Sent via SendGrid. Logged as an outcome — partner is hidden from the queue for 3 days after.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-muted/40 rounded p-2"><strong>To:</strong> {emailPartner?.email}</div>
            <div>
              <Label className="text-sm">Subject</Label>
              <Input value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="email-subject" />
            </div>
            <div>
              <Label className="text-sm">Message</Label>
              <Textarea rows={10} value={emailForm.body} onChange={e => setEmailForm({ ...emailForm, body: e.target.value })} data-testid="email-body" />
              <p className="text-xs text-muted-foreground mt-1">Default template loaded — edit anything before sending.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={emailSending} data-testid="email-send" className="bg-sky-600 hover:bg-sky-700 text-white">
              <Mail className="w-4 h-4 mr-1.5" />
              {emailSending ? 'Sending...' : 'Send email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
